import { Tooltip } from 'bootstrap';
import { debounce } from 'debounce';

import { compile, createElement } from '@syncfusion/ej2-base';
import { L10n } from '@syncfusion/ej2-base';
import { DatePicker } from '@syncfusion/ej2-calendars';
import { DataManager, UrlAdaptor } from '@syncfusion/ej2-data';
import { ComboBox, DropDownList } from '@syncfusion/ej2-dropdowns';
import {
  ActionEventArgs,
  Column,
  ColumnChooser,
  ExcelExport,
  ExcelQueryCellInfoEventArgs,
  Filter,
  FilterSearchBeginEventArgs,
  Grid,
  InfiniteScroll,
  Page,
  RecordClickEventArgs,
  Reorder,
  Resize,
  Selection,
  Sort,
  SortEventArgs,
} from '@syncfusion/ej2-grids';

import Cookies from 'js-cookie';

import { ALL_UUID } from '@/shared/constants';
import { COOKIES } from '@/shared/cookies';
import { GridButtonProps } from '@/shared/grids/columns/buttonColumn';
import { gridSettings } from '@/shared/grids/settings';
import { routes } from '@/shared/routes';
import { EditableRequestBody } from '@/shared/types/editable';
import {
  GridColumnSettings,
  GridFilterOperator,
  GridFilterPickerProps,
  GridFilterPredicate,
  GridRequestBody,
  GridResponseBody,
  GridSettings,
  GridViewPickerProps,
  UserGridSettings,
} from '@/shared/types/grid';
import { ModelName } from '@/shared/types/model';
import {
  DEFAULT_LOCALE,
  dateFormat,
  dateTimeFormat,
  dayLongMonthFormat,
  dayMonthFormat,
  dayMonthYearFormat,
  dayMonthYearTimeFormat,
  exportDateFormat,
  exportDateTimeFormat,
  timeFormat,
} from '@/shared/utils/format';
import { isNotNullOrUndefined } from '@/shared/utils/misc';
import { copy, keys, merge, omit } from '@/shared/utils/object';
import { appendQuery } from '@/shared/utils/url';

import { dispatch } from '@/front/event';
import { DELETE, GET, POST } from '@/front/fetch';
import { showOffcanvas } from '@/front/offcanvas';

L10n.load({
  [DEFAULT_LOCALE.code]: {
    grid: {
      EmptyRecord: '',
      Columnchooser: '<i class="fa-solid fa-table-columns"></i>',
      ChooseColumns: 'Choose columns',
      SelectAll: 'Show all columns',
    },
  },
});

// <gridId, grid>
const grids: Record<string, Grid> = {};

// <gridId, userGridSettings>
const userGridSettings: Record<string, UserGridSettings> = {};

// <gridId, <field, value>>
const filters: Record<string, Record<string, any> | undefined> = {};

const scrollLefts: Record<string, number> = {};

Grid.Inject(ColumnChooser, ExcelExport, Filter, InfiniteScroll, Page, Resize, Reorder, Selection, Sort);

const resizeGrid = () => {
  const ids = keys(grids);

  if (ids.length === 1) {
    const gridElement = document.getElementById(ids[0])!;

    const grid = grids[ids[0]]!;

    grid.height =
      window.innerHeight - grid.element.offsetTop - gridElement.querySelector('.e-gridheader')!.clientHeight - 20;
  }
};

window.addEventListener('resize', debounce(resizeGrid), true);

window.addEventListener('refreshGrids', () => {
  refreshGrids();
});

document.addEventListener('DOMContentLoaded', async () => {
  for (const element of [...document.querySelectorAll('.grid')]) {
    await initGrid(element as HTMLDivElement);
  }

  for (const element of [...document.querySelectorAll('.grid-column-picker')]) {
    initGridColumnPicker(element as HTMLAnchorElement);
  }

  for (const element of [...document.querySelectorAll('.grid-view-picker')]) {
    initGridViewPicker(element as HTMLDivElement);
  }

  for (const element of [...document.querySelectorAll('[data-grid-action]')]) {
    initGridAction(element as HTMLAnchorElement);
  }

  for (const element of [...document.querySelectorAll('.grid-filter-picker')]) {
    initGridFilter(element as HTMLDivElement);
  }

  for (const element of [...document.querySelectorAll('.grid')]) {
    await bindGrid(element as HTMLDivElement);
  }

  dispatch('gridsReady');
});

const initGrid = async (element: HTMLDivElement) => {
  const gridId = element.id;

  const modelName = element.dataset.model as ModelName;
  const view = element.dataset.view;

  const allowEditColumns = element.dataset.allowEditColumns === 'true';
  const allowExport = element.dataset.allowExport === 'true';
  const disabledColumns = (element.dataset.disabledColumns ?? '').split(',');

  const settings = gridSettings[modelName];

  if (!settings) {
    throw `Grid settings not found for model: ${modelName}`;
  }

  const userSettings = await loadGridSettings(gridId, view);

  const columns = copy(settings.columns).filter((column) => !disabledColumns.includes(column.uid));

  const columnUids: string[] = [];

  for (let i = 0; i < columns.length; i++) {
    const column = columns[i];

    if (!column.uid) {
      column.uid = column.field;
    }

    if (!columnUids.includes(column.uid)) {
      columnUids.push(column.uid);
    } else {
      throw `Duplicate column UID: ${column.uid}`;
    }

    // copy() doesn't copy template functions as uses JSON.stringify/parse
    if (typeof settings.columns[i].template === 'function') {
      column.template = settings.columns[i].template;
    }
  }

  // Apply column filter settings before creating grid
  applyGridColumnFilterSettings(gridId, columns);

  // Apply column settings before creating grid
  applyUserGridColumnSettings(columns, userSettings);

  const grid = new Grid({
    locale: DEFAULT_LOCALE.code,
    columns: columns.map((column) => omit(column, 'extra')),
    columnQueryMode: 'All',
    showColumnChooser: allowEditColumns,
    clipMode: 'Clip',
    allowResizing: allowEditColumns,
    allowReordering: allowEditColumns,
    allowSorting: true,
    allowFiltering: true,
    filterSettings: {
      type: 'Menu',
    },
    allowExcelExport: allowExport,
    pageSettings: { pageSize: 100 },
    enableInfiniteScrolling: true,
    infiniteScrollSettings: { initialBlocks: 1 },
    // Best estimate - avoids big jump on initial resize (can't do relative resize until grid rendered)
    // 50 = navbar height, 34 = grid header height, 20 = bottom margin
    height: window.innerHeight - (50 + element.offsetTop + 34 + 20),
    dataBound: gridDataBound(gridId),
    actionBegin: gridActionBegin(gridId, settings),
    actionComplete: gridActionComplete(gridId),
    recordClick: gridRecordClick,
    resizeStop: gridColumnResize(gridId),
    excelQueryCellInfo: gridExcelQueryCellInfo(settings),
  });

  grids[gridId] = grid;

  grid.appendTo(element as HTMLElement);
};

const bindGrid = async (element: HTMLDivElement) => {
  const gridId = element.id;

  const modelName = element.dataset.model as ModelName;
  const view = element.dataset.view;

  const settings = gridSettings[modelName];

  if (!settings) {
    throw `Grid settings not found for model: ${modelName}`;
  }

  const userSettings = await loadGridSettings(gridId, view);

  const grid = grids[gridId];

  // Apply sort settings after appending grid
  applyUserGridSortSettings(gridId, userSettings);

  // e.g. ?f_someId=123&f_someField=lessthanorequal|456
  // i.e. operator is optional and defaults to equal
  const query = new URLSearchParams(location.search);

  const filterKeys = [...query.keys()].filter((key) => key.startsWith('f_'));

  if (filterKeys.length > 0) {
    for (const filterKey of filterKeys) {
      const field = filterKey.substring(2); // Skip 'f_'

      const column = settings.columns.find((column) => column.field === field);

      if (column) {
        const parts = query.get(filterKey)!.split('|');

        // TODO greater than
        if (['isnull', 'isnotnull'].includes(parts[0])) {
          grid.filterByColumn(field, parts[0], null);
        } else {
          const operator = parts.length > 1 ? parts[0] : 'equal';

          let value: string | boolean | Date | number = parts.length > 1 ? parts[1] : parts[0];

          if (column.type === 'boolean') {
            value = value === 'true';
          } else if (column.type === 'date') {
            value = new Date(Date.parse(value));
          } else if (column.type === 'number') {
            value = parseFloat(value);
          }

          grid.filterByColumn(field, operator, value, undefined, matchCase(column, operator));
        }
      }
    }
  } else if (settings.defaultFilters) {
    for (const fieldName of Object.keys(settings.defaultFilters)) {
      const filter = settings.defaultFilters[fieldName]!;

      if (grid.getColumnByField(fieldName)) {
        grid.filterByColumn(fieldName, filter.operator ?? 'equal', filter.value, undefined, true);
      }
    }
  }

  setTimeout(() => {
    resizeGrid();
    grid.dataSource = gridDataSource({ gridId });
  }, 100);
};

const initGridButtons = () => {
  document.querySelectorAll('.grid-button').forEach((element) => initGridButton(element as HTMLButtonElement));
  document.querySelectorAll('.grid-tooltip').forEach((element) => initTooltip(element as HTMLElement));
};

const initGridButton = (element: HTMLButtonElement) => {
  element.addEventListener('click', gridButtonClick);
};

const initGridLinks = () => {
  document.querySelectorAll('.grid-link').forEach((element) => initGridLink(element as HTMLAnchorElement));
};

const initGridLink = (element: HTMLAnchorElement) => {
  element.addEventListener('click', (event) => {
    event.stopPropagation();
  });
};

export const initTooltip = (element: HTMLElement) => {
  new Tooltip(element);
};

const gridColumnResize = (gridId: string) => async () => {
  await saveGridSettings(gridId);
};

export const gridExcelQueryCellInfo = (settings: GridSettings<any>) => (args: ExcelQueryCellInfoEventArgs) => {
  const { columns } = settings;

  const { value, data } = args;

  const column = args.column.uid
    ? (columns.find((column) => column.uid === args.column.uid) ??
      columns.find((column) => column.field === args.column.uid))
    : undefined;

  if (column && data && value !== undefined) {
    const { format, template } = column;

    if (value instanceof Date) {
      if (format === undefined || typeof format === 'string') {
        let numberFormat: string | undefined;

        if (
          format === undefined ||
          [dateFormat, dayMonthFormat, dayLongMonthFormat, dayMonthYearFormat].includes(format)
        ) {
          args.value = new Date((value as Date).toDateString()); // Strip time
          numberFormat = exportDateFormat;
        } else if ([timeFormat, dateTimeFormat, dayMonthYearTimeFormat].includes(format)) {
          numberFormat = exportDateTimeFormat;
        }

        if (numberFormat) {
          args.style = {
            ...args.style,
            numberFormat,
          };
        }
      }
    } else if (typeof value === 'number') {
      args.value = value;
    } else if (typeof value === 'boolean') {
      args.value = value ? 'TRUE' : 'FALSE';
    } else if (template) {
      args.value = [...compile(template)(data)].map((node) => node.textContent).join(' ');
    }
  } else {
    args.value = '';
  }
};

const gridActionBegin = (gridId: string, settings: GridSettings<any>) => async (args: ActionEventArgs) => {
  const { requestType } = args;

  const content = grids[gridId].getRootElement().querySelector('.e-content');

  if (content) {
    // Maintain scroll left position (filtering resets)
    scrollLefts[gridId] = content.scrollLeft;
  }

  if (requestType === 'sorting') {
    const { columnName, direction } = args as SortEventArgs;

    await saveGridSettings(gridId, {
      sort: columnName
        ? {
            field: columnName,
            direction,
          }
        : {},
    });
  } else if (requestType === 'reorder') {
    await saveGridSettings(gridId);
  }
  // BUG: requestType is typed as 'filterBeforeOpen' (camel case) but is 'filterbeforeopen' (lower case) at runtime
  else if (requestType?.toLowerCase() === 'filterbeforeopen') {
    const { filterModel } = args as any as FilterSearchBeginEventArgs;

    if (filterModel) {
      // BUG: filterModel should be typed as FilterMenuRenderer not CheckBoxFilterBase
      // https://www.syncfusion.com/forums/181415/setting-operators-for-custom-filter-ui
      // https://www.syncfusion.com/feedback/42873/need-to-set-typing-for-menu-filterbeforeopen-event
      const untypedFilterModel = filterModel as any;

      const column = settings.columns.find((column) => column.uid === untypedFilterModel.col.uid);

      initFilterOperators(untypedFilterModel);

      const field = column?.field;

      const { lookup, options } = column?.extra?.filter ?? {};

      if (field && (lookup || options)) {
        if (field.endsWith('Ids')) {
          untypedFilterModel.customFilterOperators.stringOperator = [{ value: 'contains', text: 'Contains' }];
        } else {
          untypedFilterModel.customFilterOperators.stringOperator = [
            { value: 'equal', text: 'Equal' },
            { value: 'notequal', text: 'Not Equal' },
            { value: 'isnull', text: 'Empty' },
            { value: 'isnotnull', text: 'Not Empty' },
          ];
        }
      }
    }
  }
};

const gridActionComplete = (gridId: string) => () => {
  const content = grids[gridId].getRootElement().querySelector('.e-content');

  if (content) {
    // Maintain scroll left position (filtering resets)
    // (wait 10ms for other actions to complete)
    setTimeout(() => {
      content.scrollLeft = scrollLefts[gridId];
    }, 10);
  }
};

const gridDataBound = (gridId: string) => () => {
  const grid = grids[gridId];

  if (grid) {
    initGridButtons();
    initGridLinks();

    const countElementId = grid.getRootElement().dataset.countElementId;

    if (countElementId) {
      const countElement = document.getElementById(countElementId);

      if (countElement) {
        countElement.innerHTML =
          // totalDataRecordsCount returns previous count when filtered row count is 0, so check getRows().length as well
          grid.totalDataRecordsCount === 0 || grid.getRows().length === 0 ? '' : grid.totalDataRecordsCount.toString();
      }
    }
  }
};

const gridRecordClick = (args: RecordClickEventArgs) => {
  const { id } = args.rowData! as { id: string };

  if (id !== ALL_UUID) {
    const rowElement = args.row! as HTMLTableRowElement;

    const gridElement = rowElement.closest('.grid')! as HTMLDivElement;

    const grid = grids[gridElement.id]!;

    let path = location.pathname;

    // HACK
    if (path.endsWith('/archived')) {
      path = path.substring(0, path.length - '/archived'.length);
    }

    showOffcanvas({
      url: `${path}/${id}`,
      onHide: () => refreshGridRow(grid, id, rowElement),
    });
  }
};

const gridButtonClick = async (event: MouseEvent) => {
  event.preventDefault();
  event.stopPropagation();

  const button = event.currentTarget as HTMLButtonElement;

  const gridElement = button.closest('.grid')! as HTMLDivElement;

  const rowElement = button.closest('tr')! as HTMLTableRowElement;

  const grid = grids[gridElement.id]!;

  const modelName = gridElement.dataset.model as ModelName;

  const settings = gridSettings[modelName];

  if (!settings) {
    throw `Grid settings not found for model: ${modelName}`;
  }

  const props = button.dataset as GridButtonProps<any>;

  const { id, field, action, toggled } = props;

  if (action === 'setDateToToday') {
    const value = toggled === 'false';

    const editableReqBody: EditableRequestBody = {
      params: {
        name: field,
        primaryKey: id,
        value,
      },
    };

    await POST(routes.data.editable({ model: modelName }), editableReqBody);

    await refreshGridRow(grid, id, rowElement);
  }
};

export const refreshGrids = () => {
  for (const grid of Object.values(grids)) {
    grid.refresh();
  }
};

const refreshGridRow = async (grid: Grid, id: string, row: HTMLTableRowElement) => {
  const gridElement = grid.getRootElement();

  const modelName = gridElement.dataset.model as ModelName;
  const archived = gridElement.dataset.archived === 'true';

  const filters = (grid.filterModule as any).actualPredicate;

  const gridReqBody: GridRequestBody = {
    where: [
      {
        field: 'id',
        operator: 'equal',
        value: id,
        isComplex: false,
        ignoreAccent: false,
        ignoreCase: false,
      },
      // Include active filters to improve performance and ensure correct row is returned
      ...(filters
        ? Object.keys(filters)
            // HACK don't apply status filter (tasks) as this prevents refresh/display of completed task
            .filter((field) => field !== 'status')
            .flatMap((field) => filters[field].map((filter: GridFilterPredicate) => filter))
        : []),
    ],
    take: 1,
  };

  const gridResponse = await POST(
    routes.data.grid({
      modelName,
      archived,
    }),
    gridReqBody,
  );

  const gridResBody = (await gridResponse.json()) as GridResponseBody;

  const { result } = gridResBody;

  // Updated
  if (result.length > 0) {
    grid.setRowData(id, result[0]);
    initGridButtons();
  }
  // Deleted
  else {
    row.remove();
  }
};

const initGridColumnPicker = (element: HTMLAnchorElement) => {
  const id = element.dataset.gridId!;

  const grid = grids[id]!;

  const gridElement = document.getElementById(id)!;

  element.addEventListener('click', (e) => {
    e.preventDefault();
    // const x = gridElement.clientWidth / 2 - 125; // Column chooser is 250px wide
    grid.columnChooserModule.openColumnChooser(-1, 0);
  });

  const okButton = gridElement.querySelector('.e-ccdlg .e-cc_okbtn')!;

  okButton.addEventListener('click', async () => await saveGridSettings(id));

  const resetButton = document.createElement('button');

  resetButton.innerHTML = 'Reset';
  resetButton.className = 'e-cc e-control e-btn e-lib e-secondary e-flat';

  resetButton.addEventListener('click', async () => await resetGridSettings(id));

  okButton.after(resetButton);
};

const initGridViewPicker = (element: HTMLDivElement) => {
  const props = JSON.parse(element.dataset.props!) as GridViewPickerProps;

  const { gridId } = props;

  const input = element.querySelector('input');

  if (gridId && input) {
    // const grid = grids[gridId];

    const comboBox = new ComboBox({
      dataSource: [
        {
          value: 'archived',
          text: 'Archived',
        },
      ],
      // lookup
      //   ? new DataManager({
      //       url: routes.data.lookup.list({ model: lookup as ModelName }),
      //       adaptor: new UrlAdaptor(),
      //     })
      //   : options,
      fields: {
        value: 'value',
        text: 'text',
      },
      // value,
      placeholder: 'Default View',
      allowCustom: false,
      allowFiltering: true,
      popupHeight: 600,
      change: () => {
        const value = comboBox.value;

        if (value === 'archived') {
          location.href = routes.companies.list({ archived: true });
        }
      },
    });

    comboBox.appendTo(input);
  }
};

const initGridAction = (element: HTMLAnchorElement) => {
  const id = element.dataset.gridId!;
  const action = element.dataset.gridAction!;

  const grid = grids[id]!;

  const modelName = grid.element.dataset.model as ModelName;

  const settings = gridSettings[modelName]!;

  const { columns } = settings;

  element.addEventListener('click', (e) => {
    e.preventDefault();

    if (action === 'refresh') {
      grid.refresh();
    } else if (action === 'export') {
      const filename = document.title.split('-')[0].trim();

      const excludedGridColumns: Column[] = [];

      for (const column of columns.filter(
        (column) => !(column.showInColumnChooser == undefined ? true : column.showInColumnChooser),
      )) {
        const gridColumn = grid.columns[columns.indexOf(column)] as Column;

        if (gridColumn.visible) {
          gridColumn.visible = false;
          excludedGridColumns.push(gridColumn);
        }
      }

      grid.excelExportComplete = () => {
        for (const gridColumn of excludedGridColumns) {
          gridColumn.visible = true;
        }
      };

      grid.excelExport({
        fileName: `${filename}.xlsx`,
      });
    }
  });
};

const initGridFilter = (element: HTMLDivElement) => {
  const props = JSON.parse(element.dataset.props!) as GridFilterPickerProps<any>;

  const { gridId, field, operator, lookup, options, emptyValue, persistCookie, placeholder } = props;

  let value = props.value;

  const operators = ['equal', 'contains'];

  const input = element.querySelector('input');

  if (gridId && field && (lookup || options) && input) {
    const grid = grids[gridId];

    const gridFilterValue = () => {
      const predicate = (grid.filterModule as any).actualPredicate[field];

      if (predicate) {
        // Ignore non-equal operators as filter dropdowns are only for equal filters
        if (operators.includes(predicate[0].operator)) {
          return (grid.filterModule as any).values[field];
        }
      }
    };

    if (persistCookie) {
      const persistedValue = Cookies.get(COOKIES[persistCookie].name);

      if (persistedValue !== undefined) {
        value = persistedValue;
      }
    }

    // Set dropdown value to grid initial filter value (perhaps set via URL)
    else if (value === undefined) {
      value = gridFilterValue();
    }

    let focused = false;

    const filter = new ComboBox({
      dataSource: lookup ? lookupDataSource({ gridId, field, lookup }) : options,
      fields: {
        value: 'value',
        text: 'text',
      },
      sortOrder: lookup ? 'Ascending' : undefined,
      value,
      placeholder: placeholder ?? 'Select a value',
      allowCustom: false,
      allowFiltering: true,
      popupHeight: 600,
      focus: () => {
        focused = true;
      },
      blur: () => {
        focused = false;
      },
      change: () => {
        // Only apply filter when focused as value can be changed on grid actionComplete and we don't want to duplicate the request
        if (focused) {
          filterGrid(gridId, field, operator, filter.value, emptyValue);

          if (persistCookie) {
            if (filter.value || filter.value === 0 || filter.value === false) {
              Cookies.set(COOKIES[persistCookie].name, filter.value.toString());
            } else {
              Cookies.remove(COOKIES[persistCookie].name);
            }
          }
        }
      },
    });

    filter.appendTo(input);

    filterGrid(gridId, field, operator, value, emptyValue);

    // When grid is filtered, update value of this dropdown as well
    grid.addEventListener('actionComplete', (args: ActionEventArgs) => {
      if (args.action === 'filter') {
        filter.value = gridFilterValue();
      }
    });
  }
};

const saveGridSettings = async (gridId: string, data?: Partial<UserGridSettings>) => {
  const grid = grids[gridId];

  const view = grid.element.dataset.view;

  const settings: UserGridSettings = {
    columns: grid
      .getColumns()
      .map((column, index) => {
        return {
          [column.uid]: {
            index,
            visible: column.visible,
            width: column.width,
          },
        };
      })
      .reduce(merge),
    ...data,
  };

  await POST(
    routes.settings.grid({
      id: gridId,
      view,
    }),
    settings,
  );

  userGridSettings[gridId] = settings;
};

const loadGridSettings = async (gridId: string, view?: string) => {
  if (!userGridSettings[gridId]) {
    const res = await GET(
      routes.settings.grid({
        id: gridId,
        view,
      }),
    );

    userGridSettings[gridId] = (await res.json()) as UserGridSettings;
  }

  return userGridSettings[gridId];
};

const resetGridSettings = async (gridId: string) => {
  const grid = grids[gridId];

  const view = grid.element.dataset.view;

  await DELETE(
    routes.settings.grid({
      id: gridId,
      view,
    }),
  );

  delete userGridSettings[gridId];

  location.reload();
};

const applyGridColumnFilterSettings = (gridId: string, columns: GridColumnSettings<any>[]) => {
  for (const column of columns.filter((column) => column.extra?.filter)) {
    const { field } = column;
    const { lookup, options, minDate, maxDate } = column.extra!.filter!;

    if (lookup || options) {
      const getContext = (args: { column: Column }) => args.column.filter as { dropDownList?: DropDownList };

      column.filter = {
        operator: 'equal',
        ui: {
          create: (args: { target: HTMLElement; column: Column }) => {
            const context = getContext(args);

            const input = createElement('input', { className: 'flm-input' });

            args.target.appendChild(input);

            context.dropDownList = new DropDownList({
              dataSource: lookup ? lookupDataSource({ gridId, field, lookup }) : options,
              fields: {
                text: 'text',
                value: 'value',
              },
              allowFiltering: !!lookup,
              placeholder: 'Select a value',
              popupHeight: '600px',
            });

            context.dropDownList.appendTo(input);
          },
          write: (args: { column: Column; target: HTMLElement; parent: any; filteredValue: number | string }) => {
            const context = getContext(args);

            context.dropDownList!.value = args.filteredValue;
          },
          read: (args: { target: HTMLElement; column: any; operator: string; fltrObj: Filter }) => {
            const context = getContext(args);

            args.fltrObj.filterByColumn(
              args.column.field,
              args.operator,
              context.dropDownList!.value as string,
              undefined,
              matchCase(args.column, args.operator),
            );
          },
        },
      };
    } else if (minDate || maxDate) {
      const getContext = (args: { column: Column }) => args.column.filter as { datePicker?: DatePicker };

      column.filter = {
        ui: {
          create: (args: { target: HTMLElement; column: Column }) => {
            const context = getContext(args);

            const input = createElement('input', { className: 'flm-input' });

            args.target.appendChild(input);

            context.datePicker = new DatePicker({
              locale: DEFAULT_LOCALE.code,
              format: column.format,
              min: minDate,
              max: maxDate,
              placeholder: 'Choose a Date',
              showTodayButton: !(minDate || maxDate),
              showClearButton: false,
            });

            context.datePicker.appendTo(input);
          },
          write: (args: { column: Column; target: HTMLElement; parent: any; filteredValue: number | string }) => {
            const context = getContext(args);

            context.datePicker!.value = new Date(Date.parse(args.filteredValue as string));
          },
          read: (args: { target: HTMLElement; column: any; operator: string; fltrObj: Filter }) => {
            const context = getContext(args);

            args.fltrObj.filterByColumn(
              args.column.field,
              args.operator,
              context.datePicker!.value,
              undefined,
              matchCase(args.column, args.operator),
            );
          },
        },
      };
    }
  }
};

const applyUserGridColumnSettings = (columns: GridColumnSettings<any>[], settings?: UserGridSettings) => {
  const allColumnSettings = settings?.columns;

  if (allColumnSettings) {
    const columnIndex = (column: Partial<Column>) => (column.uid ? (allColumnSettings[column.uid]?.index ?? 0) : 0);

    columns.sort((columnA, columnB) => {
      const columnAIndex = columnIndex(columnA);
      const columnBIndex = columnIndex(columnB);

      return columnAIndex < columnBIndex ? -1 : columnAIndex > columnBIndex ? 1 : 0;
    });

    const uids = keys(allColumnSettings);

    // Hide any new columns and push to end
    for (const newColumn of columns.filter((column) => column.uid && !uids.includes(column.uid))) {
      newColumn.visible = false;
      columns.push(columns.splice(columns.indexOf(newColumn), 1)[0]);
    }

    for (const uid of uids) {
      const columnSettings = allColumnSettings[uid];

      const column = columns.find((column) => column.uid === uid);

      if (columnSettings && column) {
        column.visible = !!columnSettings.visible;

        if (columnSettings.width) {
          column.width = columnSettings.width;
        }
      }
    }
  }
};

const applyUserGridSortSettings = (gridId: string, settings?: UserGridSettings) => {
  if (settings?.sort?.field) {
    const grid = grids[gridId]!;

    if (grid.getColumnByField(settings.sort.field)) {
      grid.sortColumn(settings.sort.field, settings.sort.direction ?? 'Ascending');
    }
  }
};

const filterGrid = (gridId: string, field: string, operator: GridFilterOperator, value: any, emptyValue?: string) => {
  const grid = grids[gridId];

  const column = grid.getColumnByField(field);

  if (column) {
    if (emptyValue && (value === null || value === undefined)) {
      value = emptyValue;
    }

    if (value === null || value === undefined) {
      grid.clearFiltering([field]);
    } else {
      grid.filterByColumn(field, operator, value, undefined, matchCase(column, operator));
    }

    filters[gridId] = {
      ...filters[gridId],
      [field]: value,
    };
  }
};

export const getNonNullGridFilters = () => {
  const gridIds = keys(grids);

  if (gridIds.length === 1) {
    const f = filters[gridIds[0]];

    if (f) {
      const setFilters = Object.keys(f)
        .filter((field) => isNotNullOrUndefined(f[field]))
        .map((field) => {
          return { [field]: f[field] };
        });

      if (setFilters.length) {
        return setFilters.reduce(merge);
      }
    }
  }
};

const initFilterOperators = (filterModel: any) => {
  // https://ej2.syncfusion.com/aspnetmvc/documentation/grid/filtering/filtering#filter-operators
  // eslint-disable-next-line  @typescript-eslint/no-unused-expressions
  (filterModel.customFilterOperators.booleanOperator = [
    { value: 'equal', text: 'Equal' },
    { value: 'notequal', text: 'Not Equal' },
  ]),
    (filterModel.customFilterOperators.dateOperator = [
      { value: 'greaterthan', text: 'Greater Than' },
      { value: 'greaterthanorequal', text: 'Greater Than or Equal' },
      { value: 'lessthan', text: 'Less Than' },
      { value: 'lessthanorequal', text: 'Less Than or Equal' },
      { value: 'equal', text: 'Equal' },
      { value: 'notequal', text: 'Not Equal' },
      { value: 'isnull', text: 'Empty' },
      { value: 'isnotnull', text: 'Not Empty' },
    ]),
    (filterModel.customFilterOperators.numberOperator = [
      { value: 'greaterthan', text: 'Greater Than' },
      { value: 'greaterthanorequal', text: 'Greater Than or Equal' },
      { value: 'lessthan', text: 'Less Than' },
      { value: 'lessthanorequal', text: 'Less Than or Equal' },
      { value: 'equal', text: 'Equal' },
      { value: 'notequal', text: 'Not Equal' },
      { value: 'isnull', text: 'Empty' },
      { value: 'isnotnull', text: 'Not Empty' },
    ]),
    (filterModel.customFilterOperators.stringOperator = [
      { value: 'startswith', text: 'Starts With' },
      { value: 'endswith', text: 'Ends With' },
      { value: 'contains', text: 'Contains' },
      { value: 'equal', text: 'Equal' },
      { value: 'notequal', text: 'Not Equal' },
      { value: 'isnull', text: 'Empty' },
      { value: 'isnotnull', text: 'Not Empty' },
    ]);
};

const lookupDataSource = (props: { gridId: string; field: string; lookup: true | ModelName }) => {
  const { gridId, field, lookup } = props;

  return lookup === true
    ? gridDataSource({ gridId, lookup: field })
    : new DataManager({
        url: routes.data.lookup.list({ model: lookup }),
        adaptor: new UrlAdaptor(),
      });
};

const gridDataSource = (props: { gridId: string; lookup?: string }) => {
  const { gridId, lookup } = props;

  const grid = grids[gridId];

  const modelName = grid.element.dataset.model as ModelName;
  const archived = grid.element.dataset.archived === 'true';

  return new DataManager({
    url: appendQuery(routes.data.grid({ modelName, archived }), { lookup }),
    adaptor: new UrlAdaptor(),
  });
};

const matchCase = (column: Partial<Column>, operator: string) =>
  column.type === 'string' ? !['contains', 'startswith', 'endswith'].includes(operator) : undefined;
